import { useState } from 'react';

export default function KnowledgePage() {
  const [query, setQuery] = useState('');
  const [response, setResponse] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const guidelines = [
    { code: 'E-23', title: 'Model Risk Management', category: 'Model Risk' },
    { code: 'B-15', title: 'Climate Risk Management', category: 'Climate Risk' },
    { code: 'B-13', title: 'Technology and Cyber Risk', category: 'Cyber Risk' },
    { code: 'B-10', title: 'Third-Party Risk', category: 'Outsourcing' },
    { code: 'E-21', title: 'Operational Resilience', category: 'Operational Risk' },
  ];

  const handleSearch = () => {
    setLoading(true);
    // Mock AI response demonstrating E-23 compliance
    setTimeout(() => {
      setResponse({
        answer: "Guideline E-23 requires institutions to establish comprehensive model risk management frameworks grounded in three foundational principles:\n\n1. **Soundness**: Technical robustness including data quality, independent validation, continuous monitoring, and bias detection\n\n2. **Accountability**: Clear human oversight with defined roles, human-in-the-loop for material decisions, and comprehensive audit trails\n\n3. **Explainability**: Transparent reasoning showing how models process inputs to outputs, with uncertainty quantification\n\nFor AI/ML models specifically, E-23 emphasizes the need for enhanced controls due to their complexity, opacity, and potential for bias.",
        confidence: 0.95,
        sources: [
          {
            guideline: 'E-23',
            section: '4.1 - Foundational Principles',
            text: 'Model risk management must be grounded in three foundational principles: soundness, accountability, and explainability.',
            relevance: 0.98
          },
          {
            guideline: 'E-23',
            section: '5.2 - AI and Machine Learning Considerations',
            text: 'AI and machine learning models require enhanced attention due to their complexity, potential opacity, and susceptibility to bias.',
            relevance: 0.92
          }
        ],
        reasoning_chain: [
          'Query analysis: User asking about E-23 AI/ML model requirements',
          'Retrieved relevant sections from Guideline E-23',
          'Identified three foundational principles as core requirement',
          'Added AI/ML specific considerations from section 5.2',
          'Synthesized comprehensive answer with citations'
        ]
      });
      setLoading(false);
    }, 1500);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <p className="text-sm text-gray-600">AI-Powered Regulatory Knowledge Assistant</p>
        <div className="mt-2 flex items-center space-x-2">
          <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
            ✓ E-23 Compliant
          </span>
          <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
            RAG Architecture
          </span>
          <span className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-xs font-medium">
            Human-in-the-Loop
          </span>
        </div>
      </div>

      {/* Search Interface */}
      <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-6">
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Ask a question about Kenbri Group data
          </label>
          <div className="flex space-x-3">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="e.g., What does E-23 require for AI model validation?"
              className="flex-1 border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-700"
            />
            <button
              onClick={handleSearch}
              disabled={!query || loading}
              className="px-6 py-3 bg-blue-700 text-white rounded-lg hover:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              {loading ? 'Searching...' : 'Search'}
            </button>
          </div>
        </div>

        {/* Example Queries */}
        <div className="flex flex-wrap gap-2">
          <span className="text-xs text-gray-600">Examples:</span>
          {[
            'What does E-23 require for AI model validation?',
            'How should institutions report Scope 3 emissions under B-15?',
            'What are the B-13 requirements for 24-hour cyber incident reporting?'
          ].map((example, i) => (
            <button
              key={i}
              onClick={() => setQuery(example)}
              className="text-xs px-3 py-1 bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200"
            >
              {example}
            </button>
          ))}
        </div>
      </div>

      {/* Response */}
      {response && (
        <div className="space-y-4">
          {/* Answer Card */}
          <div className="bg-gradient-to-r from-blue-50 to-green-50 rounded-lg border border-blue-200 p-6">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center space-x-2">
                <span className="text-2xl">🤖</span>
                <span className="font-semibold text-gray-900">AI Response</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600">Confidence:</span>
                <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-semibold">
                  {(response.confidence * 100).toFixed(0)}%
                </span>
              </div>
            </div>
            <div className="prose max-w-none">
              <p className="text-gray-800 whitespace-pre-line">{response.answer}</p>
            </div>
          </div>

          {/* Sources (E-23 Explainability) */}
          <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-6">
            <div className="flex items-center space-x-2 mb-4">
              <span className="text-xl">📚</span>
              <h3 className="text-lg font-semibold text-gray-900">Sources (E-23 Explainability)</h3>
            </div>
            <div className="space-y-3">
              {response.sources.map((source: any, i: number) => (
                <div key={i} className="border-l-4 border-blue-500 bg-gray-50 p-4 rounded">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-mono text-sm font-semibold text-blue-700">
                      Guideline {source.guideline} - {source.section}
                    </span>
                    <span className="text-xs text-gray-600">
                      Relevance: {(source.relevance * 100).toFixed(0)}%
                    </span>
                  </div>
                  <p className="text-sm text-gray-700 italic">"{source.text}"</p>
                </div>
              ))}
            </div>
          </div>

          {/* Reasoning Chain (E-23 Soundness) */}
          <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-6">
            <div className="flex items-center space-x-2 mb-4">
              <span className="text-xl">🔍</span>
              <h3 className="text-lg font-semibold text-gray-900">Reasoning Chain (E-23 Soundness)</h3>
            </div>
            <ol className="space-y-2">
              {response.reasoning_chain.map((step: string, i: number) => (
                <li key={i} className="flex items-start space-x-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-800 rounded-full flex items-center justify-center text-xs font-semibold">
                    {i + 1}
                  </span>
                  <span className="text-sm text-gray-700">{step}</span>
                </li>
              ))}
            </ol>
          </div>

          {/* Human Review (E-23 Accountability) */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
            <div className="flex items-start space-x-3">
              <span className="text-2xl">👤</span>
              <div className="flex-1">
                <h4 className="font-semibold text-gray-900 mb-2">Human Review Required (E-23 Accountability)</h4>
                <p className="text-sm text-gray-700 mb-3">
                  This AI response should be reviewed by a qualified supervisor before being used for
                  supervisory decisions or regulatory interpretation.
                </p>
                <div className="flex space-x-3">
                  <button className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium">
                    ✓ Approve Response
                  </button>
                  <button className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm font-medium">
                    ✏️ Request Revision
                  </button>
                  <button className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 text-sm font-medium">
                    ✗ Reject
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Guidelines Reference */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Business Intelligence</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {guidelines.map((guideline) => (
            <div key={guideline.code} className="bg-white shadow-sm rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-2">
                <span className="font-mono font-bold text-blue-700">{guideline.code}</span>
                <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs">
                  {guideline.category}
                </span>
              </div>
              <h4 className="text-sm font-medium text-gray-900">{guideline.title}</h4>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
